import { afterNextRender, Component, computed, effect, ElementRef, inject, input, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { scaleLinear } from 'd3-scale';
import { v4 as uuid } from 'uuid';

import { ChartTypes, EChartTypes, MapTypes, TableTypes, TTChartTypes } from '@23degrees/shared-code/config/constants';
import type { Content } from '@23degrees/shared-code/models';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { ResizeObserverClass } from '@23degrees/shared/classes/resizeObserver.class';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { ResponsiveStylePipe } from '@23degrees/shared/pipes';
import { StyleService } from '@23degrees/shared/services/style';
import { TrackerService } from '@23degrees/shared/services/tracker/tracker.service';

import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';

import type { IContentSelectorOptions } from '../index';
import { SmartAtlas3Component } from '../smart/smart-atlas3/smart-atlas3.component';
import { SmartChart3Component } from '../smart/smart-chart3/smart-chart3.component';
import { SmartDashComponent } from '../smart/smart-dash/smart-dash.component';
import { SmartMap2Component } from '../smart/smart-map2/smart-map2.component';
import { SmartReportComponent } from '../smart/smart-report/smart-report.component';
import { SmartSourceComponent } from '../smart/smart-source/smart-source.component';
import { SmartSSRComponent } from '../smart/smart-ssr/smart-ssr.component';
import { SmartTableComponent } from '../smart/smart-table/smart-table.component';
import { ContentDataStore } from './content-selector2.store';

@Component({
  selector: 'content-selector2',
  templateUrl: './content-selector2.component.html',
  styleUrls: ['./content-selector2.component.scss'],
  providers: [StyleService, ContentDataStore, ResponsiveStylePipe],
  standalone: true,
  imports: [
    SmartAtlas3Component,
    SmartDashComponent,
    SmartReportComponent,
    SmartChart3Component,
    SmartMap2Component,
    SmartTableComponent,
    SmartSourceComponent,
    SmartSSRComponent,
  ],
  host: {
    '[id]': '"cs-" +referenceId()',
  },
})
export class ContentSelector2Component extends BaseClass {
  // inputs
  public slug = input.required<string>();
  public options = input<IContentSelectorOptions>({});
  public displayScale = input(1);

  // outputs
  public contentChange = output<{ content: Content; referenceId: string }>();

  // content derives
  // public content = signal<Content>(undefined);
  public contentSlug = computed(() => this.content()?.slug);
  public contentType = computed(() => this.content()?.type);
  public isAtlas = computed(() => this.contentType() === 'atlas');
  public isChart = computed(() => [...ChartTypes, ...TTChartTypes, ...EChartTypes].includes(this.contentType()));
  public isDash = computed(() => this.contentType() === 'dash');
  public isTable = computed(() => TableTypes.includes(this.contentType()));
  public isReport = computed(() => this.contentType() === 'report');
  public isMap = computed(() => MapTypes.includes(this.contentType()));
  public isDataSet = computed(() => this.contentType() === 'dataset');

  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private messageBus = inject(MessageBus);
  private breakPointManager = inject(BreakPointManager);
  private trackerService = inject(TrackerService);
  private resizeObs = inject(ResizeObserverClass);
  private styleService = inject(StyleService);
  private contentDataStore = inject(ContentDataStore);

  public content = this.contentDataStore.content;
  public referenceId = signal(uuid());
  public exportId = `export-${this.referenceId()}`;

  public reloadMessage = toSignal(
    this.messageBus.listen<{ slug: string; options: unknown }>(
      [REQUEST_MESSAGES.ReloadContentBySlug, RESPONSE_MESSAGES.BotExecuteFunction],
      MESSAGE_RECIPIENTS.ContentSelector,
      this.referenceId(),
    ),
  );

  public contentBySlugMessage = toSignal(
    this.messageBus.listen<{ content: Content; options: unknown; slug: string }>(
      [RESPONSE_MESSAGES.ContentBySlug],
      MESSAGE_RECIPIENTS.ContentSelector,
      this.referenceId(),
    ),
  );

  public renderStartMessage = toSignal(this.messageBus.listen(['RENDER_START'], MESSAGE_RECIPIENTS.ContentSelector, this.referenceId()));
  public renderFinishMessage = toSignal(this.messageBus.listen(['RENDER_FINISH'], MESSAGE_RECIPIENTS.ContentSelector, this.referenceId()));

  constructor() {
    super();

    effect(() => {
      const slug = this.slug();
      if (slug) {
        untracked(() => {
          this.reloadContent({ slug });
        });
      }
    });

    effect(() => {
      const msg = this.contentBySlugMessage();
      if (msg) {
        untracked(() => {
          this.receiveContent({ content: msg.content });
        });
      }
    });

    effect(() => {
      const msg = this.reloadMessage();
      if (msg) {
        untracked(() => {
          this.reloadContent(msg);
        });
      }
    });

    effect(() => {
      const msg = this.renderStartMessage();
      if (msg) {
        this.renderStart();
      }
    });

    effect(() => {
      const msg = this.renderFinishMessage();
      if (msg) {
        this.renderFinish();
      }
    });

    effect(() => {
      const content = this.content();
      const breakPoints = content?.typeSpecific?.style?.breakPoints;
      if (breakPoints) {
        untracked(() => {
          this.breakPointManager.updateSizeFactor();
        });
      }
    });

    afterNextRender(() => {
      this.resizeObs.observe(this.elementRef, this.resizeEvent);
    });
  }

  public resizeEvent = (entry: ResizeObserverEntry) => {
    if (!this.isServer()) {
      this.requestParentResize();
      this.breakPointManager.updateSizeFactor();
    }
  };

  private requestParentResize = () => {
    if (this.isServer() || !this.content()) return;

    // export can have top level childs
    this.messageBus.emit(REQUEST_MESSAGES.ContentResize, [], {});

    if (this.content().typeSpecific.isChild) return;

    const { breakPoints } = this.content().typeSpecific.style;
    const { blockPostMessages } = this.content().localStorage;

    const domain = breakPoints.values.map((obj) => obj.domain);
    const range = breakPoints.values.map((obj) => obj.aspectRatio);

    const sizeScale = scaleLinear().clamp(true);
    sizeScale.domain(domain).range(range);

    const { clientWidth, clientHeight } = this.elementRef.nativeElement;
    const aspectRatio = sizeScale(clientWidth);
    const dynamicHeight = Math.floor(clientWidth / aspectRatio);

    try {
      if (Number.isFinite(dynamicHeight)) {
        if (!blockPostMessages && window?.parent) {
          // we have a dynamic height, don't emit regular height
          if (!this.content().localStorage.dynamicHeightAMP) {
            this.trackerService.postMessage({ sentinel: 'amp', type: 'embed-size', height: dynamicHeight });
          }

          this.trackerService.postMessage({ action: 'contentSize', height: dynamicHeight, contentSlug: this.contentSlug() });
        }

        if (this.elementRef.nativeElement.hasAttribute('data-size-me')) {
          this.elementRef.nativeElement.style.minHeight = `${dynamicHeight}px`;
        }
      }
    } catch (error) {
      console.log('[CS2] reqResize error', error);
    }
  };

  private renderStart() {
    this.trackerService.postMessage({ action: 'contentRenderStart', contentSlug: this.contentSlug() });
  }

  private renderFinish() {
    this.trackerService.postMessage({ action: 'contentRenderFinish', contentSlug: this.contentSlug() });
  }

  private handleFilterParams = (content: Content) => {
    const { branch, filter } = this.globalService.getParameters();

    // const filter = `?filter=${btoa(encodeURIComponent(JSON.stringify({ fieldName: 'a0', filterValue: 'Wien' })))}`;
    // const filter = `?filter=${btoa(encodeURIComponent(JSON.stringify({ a0: 'Wien' })).replace(/=/g, ''))}`;

    if (filter) {
      try {
        let filterBase64 = filter;
        if (filterBase64.length % 4 !== 0) {
          filterBase64 += '='.repeat(4 - (filterBase64.length % 4));
        }

        const filterStr = decodeURIComponent(atob(filterBase64));
        if (filterStr.startsWith('{') && filterStr.endsWith('}')) {
          const filterValue = JSON.parse(filterStr);
          const filterOptions: { slug: string; branchFilter?: unknown; filterVisual?: unknown } = { slug: content.slug };

          if (filterValue.fieldName && filterValue.filterValue) {
            filterOptions.filterVisual = filterValue;
            content.localStorage.filterVisual = filterValue;
          } else {
            filterOptions.branchFilter = filterValue;
            content.localStorage.branchFilter = filterValue;
          }

          this.messageBus.emit(REQUEST_MESSAGES.ContentBySlug, [MESSAGE_RECIPIENTS.Editor], filterOptions);
        }
      } catch (error) {
        console.log('[SearchFilter] Error during options parsing:', error);
      }
    }
  };

  private handleSearchParams = (content: Content) => {
    const searchParams = this.globalService.getSearchParameters();

    // custom resize parameter for tables
    if (TableTypes.includes(content.type)) {
      const resizeStr = searchParams?.get('resize');
      if (resizeStr) {
        if (['true', '1'].includes(resizeStr)) {
          content.typeSpecific.table.settings.dynamicHeight = true;
          content.typeSpecific.table2.style.dynamicHeight = true;
        }

        if (['false', '0'].includes(resizeStr)) {
          content.typeSpecific.table.settings.dynamicHeight = false;
          content.typeSpecific.table2.style.dynamicHeight = false;
        }
      }
    }
  };

  private receiveContent = (msg: { content: Content; options?: unknown }) => {
    const { content, options } = msg;

    if (this.slug() !== content.slug) {
      console.log('[CS2] wrong slug, abort!', this.slug(), content.slug);
      return;
    }

    this.contentDataStore.updateContent(content);
    this.contentChange.emit({ content, referenceId: this.referenceId() });

    this.handleFilterParams(content);
    this.handleSearchParams(content);

    this.styleService.setContent(content);
    this.breakPointManager.setContainerRef(this.elementRef.nativeElement, content);
  };

  /**
   * Single Entry for reloading complete content including editor also restarts complete datapipeline
   */
  private reloadContent = (msg) => {
    // console.log('[CS2] reload content', msg);
    const { slug, options } = msg;

    if (this.slug() !== slug) {
      console.log('[CS2] wrong slug, abort!', this.slug(), slug);
      return;
    }

    // this.contentDataStore.resetContent();

    // dashboard
    if (Array.isArray(options?.specificChildUpate)) {
      this.options().childUpdates = options.specificChildUpate.map((obj) => obj.childSlug);
    } else {
      delete this.options().childUpdates;
    }

    const isPublic = this.options().public || this.isServer();
    const checkForUpdate = options?.checkForUpdate ?? !isPublic;
    const baseRecipients = [MESSAGE_RECIPIENTS.ContentSelector, MESSAGE_RECIPIENTS.Editor];
    const visualRecipients = [MESSAGE_RECIPIENTS.Chart, MESSAGE_RECIPIENTS.Map];
    const parentRecipients = [MESSAGE_RECIPIENTS.Atlas, MESSAGE_RECIPIENTS.Dash, MESSAGE_RECIPIENTS.Report];
    const recipients = [...baseRecipients, ...visualRecipients, ...parentRecipients, MESSAGE_RECIPIENTS.Source];

    const reqOptions = {
      options: options?.requestRenderData ? options : {},
      slug: slug,
      public: isPublic,
      checkForUpdate: checkForUpdate,
    };

    this.messageBus.emit(REQUEST_MESSAGES.ContentBySlug, recipients, reqOptions, this.referenceId());
  };
}
